import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { Drawer as DrawerPrimitive } from "vaul"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

// Context to share mobile state across all components
const MobileContext = React.createContext(false)

// Hook to detect mobile devices - primarily based on screen width for reliability
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(() => {
    // Initial check - safe for SSR
    if (typeof window === 'undefined') return false
    // Use 768px breakpoint (standard tablet/mobile breakpoint)
    return window.innerWidth < 768
  })

  React.useEffect(() => {
    const checkMobile = () => {
      // Primary: screen width < 768px
      // Secondary: touch capability for tablets in portrait
      const isSmallScreen = window.innerWidth < 768
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      const isMobileDevice = isSmallScreen || (hasTouch && window.innerWidth < 1024)
      setIsMobile(isMobileDevice)
    }
    
    // Run immediately after mount
    checkMobile()
    
    // Also check on resize
    window.addEventListener('resize', checkMobile)
    // Check on orientation change (important for mobile)
    window.addEventListener('orientationchange', checkMobile)
    
    return () => {
      window.removeEventListener('resize', checkMobile)
      window.removeEventListener('orientationchange', checkMobile)
    }
  }, [])

  return isMobile
}

// Responsive Dialog - ALWAYS uses Drawer on mobile for swipe support
const SwipeableDialog = ({ children, open, onOpenChange, ...props }) => {
  const isMobile = useIsMobile()
  
  // Use Drawer (vaul) on mobile - it has built-in swipe to close
  if (isMobile) {
    return (
      <DrawerPrimitive.Root 
        open={open} 
        onOpenChange={onOpenChange}
        shouldScaleBackground={false}
        {...props}
      >
        <MobileContext.Provider value={true}>
          {children}
        </MobileContext.Provider>
      </DrawerPrimitive.Root>
    )
  }
  
  // Use Dialog on desktop
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} {...props}>
      <MobileContext.Provider value={false}>
        {children}
      </MobileContext.Provider>
    </DialogPrimitive.Root>
  )
}

const SwipeableDialogTrigger = React.forwardRef(({ className, ...props }, ref) => {
  const isMobile = React.useContext(MobileContext)
  const Component = isMobile ? DrawerPrimitive.Trigger : DialogPrimitive.Trigger
  return <Component ref={ref} className={className} {...props} />
})
SwipeableDialogTrigger.displayName = "SwipeableDialogTrigger"

const SwipeableDialogClose = React.forwardRef(({ className, ...props }, ref) => {
  const isMobile = React.useContext(MobileContext)
  const Component = isMobile ? DrawerPrimitive.Close : DialogPrimitive.Close
  return <Component ref={ref} className={className} {...props} />
})
SwipeableDialogClose.displayName = "SwipeableDialogClose"

// Mobile Drawer Content with swipe support
const MobileDrawerContent = React.forwardRef(({ className, children, showCloseButton = true, ...props }, ref) => (
  <DrawerPrimitive.Portal>
    <DrawerPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/80" />
    <DrawerPrimitive.Content
      ref={ref}
      className={cn(
        "fixed inset-x-0 bottom-0 z-[100] mt-24 flex h-auto max-h-[96vh] flex-col rounded-t-[20px] border bg-white",
        className
      )}
      {...props}
    >
      {/* Drag Handle - IMPORTANT for swipe gesture */}
      <div className="mx-auto mt-4 h-1.5 w-[60px] shrink-0 rounded-full bg-gray-300" />
      
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6 pb-8">
        {children}
      </div>
      
      {showCloseButton && (
        <DrawerPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none">
          <X className="h-5 w-5" />
          <span className="sr-only">Schließen</span>
        </DrawerPrimitive.Close>
      )}
    </DrawerPrimitive.Content>
  </DrawerPrimitive.Portal>
))
MobileDrawerContent.displayName = "MobileDrawerContent"

// Desktop Dialog Content
const DesktopDialogContent = React.forwardRef(({ className, children, showCloseButton = true, ...props }, ref) => (
  <DialogPrimitive.Portal>
    <DialogPrimitive.Overlay className="fixed inset-0 z-[100] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-[100] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg max-h-[90vh] overflow-y-auto",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none">
          <X className="h-4 w-4" />
          <span className="sr-only">Schließen</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPrimitive.Portal>
))
DesktopDialogContent.displayName = "DesktopDialogContent"

// Main Content component - switches based on context
const SwipeableDialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const isMobile = React.useContext(MobileContext)
  
  if (isMobile) {
    return <MobileDrawerContent ref={ref} className={className} {...props}>{children}</MobileDrawerContent>
  }
  
  return <DesktopDialogContent ref={ref} className={className} {...props}>{children}</DesktopDialogContent>
})
SwipeableDialogContent.displayName = "SwipeableDialogContent"

const SwipeableDialogHeader = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props}
  />
)
SwipeableDialogHeader.displayName = "SwipeableDialogHeader"

const SwipeableDialogFooter = ({ className, ...props }) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4", className)}
    {...props}
  />
)
SwipeableDialogFooter.displayName = "SwipeableDialogFooter"

const SwipeableDialogTitle = React.forwardRef(({ className, ...props }, ref) => {
  const isMobile = React.useContext(MobileContext)
  const Component = isMobile ? DrawerPrimitive.Title : DialogPrimitive.Title
  
  return (
    <Component
      ref={ref}
      className={cn("text-lg font-semibold leading-none tracking-tight", className)}
      {...props}
    />
  )
})
SwipeableDialogTitle.displayName = "SwipeableDialogTitle"

const SwipeableDialogDescription = React.forwardRef(({ className, ...props }, ref) => {
  const isMobile = React.useContext(MobileContext)
  const Component = isMobile ? DrawerPrimitive.Description : DialogPrimitive.Description
  
  return (
    <Component
      ref={ref}
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
})
SwipeableDialogDescription.displayName = "SwipeableDialogDescription"

export {
  SwipeableDialog,
  SwipeableDialogTrigger,
  SwipeableDialogClose,
  SwipeableDialogContent,
  SwipeableDialogHeader,
  SwipeableDialogFooter,
  SwipeableDialogTitle,
  SwipeableDialogDescription,
  useIsMobile,
}
